import React, { useState } from 'react';
import { Modal, Image } from 'react-bootstrap';
import { Package } from 'lucide-react';

interface ImageGalleryProps {
  productImages?: string[] | undefined;
  learnMoreImages?: string[] | undefined;
  title?: string;
  mainImageSize?: { width: number; height: number };
  thumbnailSize?: { width: number; height: number };
  showThumbnails?: boolean;
  maxThumbnails?: number;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  productImages,
  learnMoreImages,
  title = 'Image Gallery',
  mainImageSize = { width: 120, height: 120 },
  thumbnailSize = { width: 38, height: 38 },
  showThumbnails = true,
  maxThumbnails = 5,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const uniqueProductImages = Array.from(new Set(productImages || []));
  const uniqueLearnMoreImages = Array.from(new Set(learnMoreImages || []));
  const totalImages = uniqueProductImages.length + uniqueLearnMoreImages.length;

  // Show placeholder when no images available
  if (totalImages === 0) {
    return (
      <div
        className="rounded d-flex align-items-center justify-content-center"
        style={{
          width: `${mainImageSize.width}px`,
          height: `${mainImageSize.height}px`,
          backgroundColor: '#f8f9fa',
          border: '1px solid #dee2e6',
        }}
      >
        <Package size={mainImageSize.width > 80 ? 24 : 20} className="text-muted" />
      </div>
    );
  }

  const mainImage = uniqueProductImages[0] || uniqueLearnMoreImages[0];
  const firstLearnMoreImage = uniqueLearnMoreImages[0];
  const thumbnails = [
    ...uniqueProductImages.slice(1, maxThumbnails + 1),
    ...uniqueLearnMoreImages.slice(0, Math.max(0, maxThumbnails + 1 - uniqueProductImages.length)),
  ].slice(0, maxThumbnails);
  const remainingCount = totalImages - 1 - thumbnails.length;

  const handleImageError = (url: string) => {
    setFailedImages((prev) => new Set(prev).add(url));
  };

  return (
    <>
      <div className="d-flex" style={{ gap: '8px' }}>
        {/* Left Side: Featured Image and Thumbnails Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(3, ${thumbnailSize.width}px)`, gap: '2px' }}>
          {/* Featured Image (spans 3 columns, top row) */}
          <div
            className="position-relative"
            style={{ gridColumn: '1 / -1', cursor: 'pointer' }}
            onClick={() => setShowModal(true)}
          >
            {mainImage && failedImages.has(mainImage) ? (
              <div
                className="rounded d-flex align-items-center justify-content-center"
                style={{
                  width: `${thumbnailSize.width * 3 + 4}px`,
                  height: `${mainImageSize.height}px`,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                }}
              >
                <Package size={mainImageSize.width > 80 ? 24 : 20} className="text-muted" />
              </div>
            ) : (
              mainImage && (
                <Image
                  src={mainImage}
                  alt="Featured Product"
                  width={thumbnailSize.width * 3 + 4}
                  height={mainImageSize.height}
                  className="rounded"
                  style={{ objectFit: 'cover', border: '1px solid #dee2e6', width: '100%' }}
                  onError={() => handleImageError(mainImage)}
                />
              )
            )}
          </div>

          {/* Thumbnail Grid (below featured image) */}
          {showThumbnails &&
            thumbnails.map((url, index) =>
              failedImages.has(url) ? (
                <div
                  key={index}
                  className="rounded d-flex align-items-center justify-content-center"
                  style={{
                    width: `${thumbnailSize.width}px`,
                    height: `${thumbnailSize.height}px`,
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowModal(true)}
                >
                  <Package size={16} className="text-muted" />
                </div>
              ) : (
                <Image
                  key={index}
                  src={url}
                  alt={`Thumbnail ${index + 2}`}
                  width={thumbnailSize.width}
                  height={thumbnailSize.height}
                  className="rounded"
                  style={{ objectFit: 'cover', cursor: 'pointer', border: '1px solid #dee2e6' }}
                  onClick={() => setShowModal(true)}
                  onError={() => handleImageError(url)}
                />
              ),
            )}

          {/* Remaining count badge */}
          {showThumbnails && remainingCount > 0 && (
            <div
              className="rounded d-flex align-items-center justify-content-center"
              style={{
                width: `${thumbnailSize.width}px`,
                height: `${thumbnailSize.height}px`,
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                fontSize: '0.7rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                border: '1px solid #dee2e6',
              }}
              onClick={() => setShowModal(true)}
            >
              +{remainingCount}
            </div>
          )}
        </div>

        {/* Right End: Long PDP Image */}
        {showThumbnails && firstLearnMoreImage && (
          <div className="position-relative">
            {failedImages.has(firstLearnMoreImage) ? (
              <div
                className="rounded d-flex align-items-center justify-content-center"
                style={{
                  width: '40px',
                  height: `${
                    mainImageSize.height +
                    (thumbnails.length > 0
                      ? Math.ceil(thumbnails.length / 3) * thumbnailSize.height + Math.ceil(thumbnails.length / 3) * 2
                      : 0)
                  }px`,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  cursor: 'pointer',
                }}
                onClick={() => setShowModal(true)}
              >
                <Package size={20} className="text-muted" />
              </div>
            ) : (
              <Image
                src={firstLearnMoreImage}
                alt="PDP 1"
                width={40}
                height={
                  mainImageSize.height +
                  (thumbnails.length > 0
                    ? Math.ceil(thumbnails.length / 3) * thumbnailSize.height + Math.ceil(thumbnails.length / 3) * 2
                    : 0)
                }
                className="rounded"
                style={{
                  objectFit: 'cover',
                  cursor: 'pointer',
                  border: '1px solidrgb(166, 173, 180)',
                }}
                onClick={() => setShowModal(true)}
                onError={() => handleImageError(firstLearnMoreImage)}
              />
            )}
            {uniqueLearnMoreImages.length > 1 && (
              <div
                className="position-absolute bottom-0 end-0 m-1 px-1"
                style={{
                  backgroundColor: 'rgba(112, 156, 223, 0.9)',
                  color: 'white',
                  fontSize: '0.6rem',
                  fontWeight: 'bold',
                  borderRadius: '2px',
                }}
              >
                +{uniqueLearnMoreImages.length - 1}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Gallery Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            {/* Product Images */}
            {uniqueProductImages.length > 0 && (
              <div>
                <h6 className="mb-3">Product Images ({uniqueProductImages.length})</h6>
                <div className="d-flex flex-wrap gap-2">
                  {uniqueProductImages.map((url, index) => (
                    <div key={index} className="position-relative mb-2">
                      <Image
                        src={url}
                        alt={`Product ${index + 1}`}
                        width={170}
                        height={170}
                        className="rounded"
                        style={{
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(url, '_blank')}
                      />
                      <div
                        className="position-absolute top-0 end-0 m-1 px-1 text-white"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          fontSize: '0.7rem',
                          borderRadius: '2px',
                        }}
                      >
                        #{index + 1}
                      </div>
                      <div className="mt-1" style={{ width: '150px' }}>
                        <small className="text-muted d-block" style={{ fontSize: '0.6rem', wordBreak: 'break-all' }}>
                          {url}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Learn More Images */}
            {uniqueLearnMoreImages.length > 0 && (
              <div className={uniqueProductImages.length > 0 ? 'mt-4' : ''}>
                <h6 className="mb-3">Learn More Images ({uniqueLearnMoreImages.length})</h6>
                <div className="d-flex flex-wrap gap-2">
                  {uniqueLearnMoreImages.map((url, index) => (
                    <div key={index} className="position-relative mb-2">
                      <Image
                        src={url}
                        alt={`PDP ${index + 1}`}
                        width={300}
                        height={600}
                        className="rounded"
                        style={{
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                        onClick={() => window.open(url, '_blank')}
                      />
                      <div
                        className="position-absolute top-0 end-0 m-1 px-1 text-white"
                        style={{
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          fontSize: '0.7rem',
                          borderRadius: '2px',
                        }}
                      >
                        #{index + 1}
                      </div>
                      <div className="mt-1" style={{ width: '150px' }}>
                        <small className="text-muted d-block" style={{ fontSize: '0.6rem', wordBreak: 'break-all' }}>
                          {url}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};
